/**
 * Candidate M — the conditional composite MSE (Registration R-11,
 * Decision Log Addendum 102). The construction is the reviewer of
 * record's Section 1 salvage (his reply of 2026-10-27 to the
 * Decision 81 / Candidate K package): the area-specific, or
 * conditional, MSE estimator — error measured against the direct
 * estimate rather than the model's own center, with the
 * (1 - gamma)^2 psi overcount corrected — usable only as a
 * composite: a precision-weighted blend with the model MSE, with a
 * floor, and with the weights registered before any run.
 *
 * For in-sample area i of a fitted cell, on the model's fitting
 * scale, with y = thetaHat (the transformed direct estimate),
 * psi the sampling variance the fit consumed, gamma the fit's
 * shrinkage weight sigma2v / (sigma2v + psi), x'beta =
 * syntheticTheta, r = y - x'beta the realized residual, and
 * mseTheta = g1 + g2 + 2*g3 the shipped analytic MSE:
 *
 *   The reviewer's conditional form is
 *     gamma^2 psi + (1 - gamma)^2 [ (y - x'beta)^2 - psi ],
 *   plus estimation terms. On this engine the estimation terms are
 *   the shipped g2 + 2*g3, and the identity
 *     g1 = gamma * psi = gamma^2 psi + (1 - gamma)^2 sigma2v
 *   holds exactly (gamma = sigma2v / (sigma2v + psi) by the
 *   fitter's construction), so the conditional estimator is
 *     mseCond = mseTheta + (1 - gamma)^2 [ r^2 - psi - sigma2v ].
 *
 *   The precision weight is the inverse-variance weight between
 *   the two estimators of the area's squared random effect: the
 *   model's sigma2v (variance 2 sigma2v^2 under the fitted normal
 *   law) and the realized r^2 - psi (variance
 *   2 (sigma2v + psi)^2). Hence
 *     w = sigma2v^2 / (sigma2v^2 + (sigma2v + psi)^2)
 *       = gamma^2 / (1 + gamma^2),  in [0, 1/2].
 *
 *   The composite (the reported MSE) is
 *     mseComp = (1 - w) mseTheta + w mseCond
 *             = mseTheta + w (1 - gamma)^2 [ r^2 - psi - sigma2v ],
 *   floored at the conditional-variance component:
 *     mseM = max(mseComp, gamma^2 psi).
 *
 * Out-of-sample areas carry no direct estimate; the conditional
 * estimator is undefined there and their shipped synthetic mseTheta
 * stands. The function is pure: it reads the fit record the fitter
 * produced and returns the replacement mseTheta per area.
 */
/** The registered precision weight, w = gamma^2 / (1 + gamma^2). */
export function conditionalWeight(gamma) {
    const g2 = gamma * gamma;
    return g2 / (1 + g2);
}
/**
 * The conditional (area-specific) MSE estimator of the reviewer's
 * printed form, plus the shipped estimation terms (g2 + 2*g3):
 *   mseTheta + (1 - gamma)^2 [ r^2 - psi - sigma2v ].
 * Defined for in-sample areas only; the caller handles the rest.
 */
export function conditionalMseTheta(area, sigma2v) {
    const gamma = area.gamma;
    const psi = area.psi;
    const r = area.thetaHat - area.syntheticTheta;
    const oneMinusGamma = 1 - gamma;
    return area.mseTheta + oneMinusGamma * oneMinusGamma * (r * r - psi - sigma2v);
}
/**
 * The floored composite MSE for one in-sample area:
 *   max( mseTheta + w (1 - gamma)^2 [ r^2 - psi - sigma2v ],
 *        gamma^2 psi ).
 */
export function compositeMseTheta(area, sigma2v) {
    const gamma = area.gamma;
    const psi = area.psi;
    const w = conditionalWeight(gamma);
    // (1 - w) mseTheta + w mseCond, in the algebraically identical
    // computed form mseTheta + w (mseCond - mseTheta).
    const composite = area.mseTheta + w * (conditionalMseTheta(area, sigma2v) - area.mseTheta);
    return Math.max(composite, gamma * gamma * psi);
}
/**
 * The composite mseTheta for every area of a fitted cell, keyed by
 * area id. In-sample areas get the floored composite; out-of-sample
 * areas keep their shipped synthetic mseTheta unchanged.
 */
export function conditionalCompositeMseTheta(fit) {
    const out = new Map();
    for (const area of fit.areas) {
        if (area.outOfSample || area.thetaHat === undefined || area.psi === undefined) {
            out.set(area.areaId, area.mseTheta);
        }
        else {
            out.set(area.areaId, compositeMseTheta(area, fit.sigma2v));
        }
    }
    return out;
}
//# sourceMappingURL=conditional-mse.js.map